import {
  useCallback,
  useEffect,
  useId,
  useLayoutEffect,
  useRef,
  useState,
  type CSSProperties,
  type ReactNode,
} from 'react';
import { createPortal } from 'react-dom';
import styles from './Tooltip.module.css';

export type TooltipPlacement = 'right' | 'left' | 'top' | 'bottom';

export type TooltipProps = {
  content: ReactNode;
  children: ReactNode;
  /** Delay antes de mostrar (ms). Padrao 120 — bem mais rapido que o title nativo. */
  delayMs?: number;
  placement?: TooltipPlacement;
  disabled?: boolean;
  /** Classe extra no wrapper do trigger (ex.: largura total na sidebar). */
  triggerClassName?: string;
  /** Mantem o tip em uma unica linha (datas/horas). */
  nowrap?: boolean;
};

type Coords = { top: number; left: number; arrowShift: number };

const VIEW_MARGIN = 8;

function computePosition(rect: DOMRect, placement: TooltipPlacement): Coords {
  const gap = 8;
  switch (placement) {
    case 'left':
      return { top: rect.top + rect.height / 2, left: rect.left - gap, arrowShift: 0 };
    case 'top':
      return { top: rect.top - gap, left: rect.left + rect.width / 2, arrowShift: 0 };
    case 'bottom':
      return { top: rect.bottom + gap, left: rect.left + rect.width / 2, arrowShift: 0 };
    case 'right':
    default:
      return { top: rect.top + rect.height / 2, left: rect.right + gap, arrowShift: 0 };
  }
}

/** Mantem o tip dentro da viewport sem cortar nas laterais/topo/baixo. */
function clampToViewport(
  desired: Coords,
  tip: DOMRect,
  placement: TooltipPlacement,
): Coords {
  let { top, left, arrowShift } = desired;

  if (placement === 'top' || placement === 'bottom') {
    const half = tip.width / 2;
    const minCenter = VIEW_MARGIN + half;
    const maxCenter = Math.max(minCenter, window.innerWidth - VIEW_MARGIN - half);
    const clamped = Math.min(Math.max(left, minCenter), maxCenter);
    arrowShift = left - clamped;
    left = clamped;

    if (placement === 'top') {
      // tip sobe com translateY(-100%); topo visual = top - height
      if (top - tip.height < VIEW_MARGIN) {
        top = VIEW_MARGIN + tip.height;
      }
    } else if (top + tip.height > window.innerHeight - VIEW_MARGIN) {
      top = Math.max(VIEW_MARGIN, window.innerHeight - VIEW_MARGIN - tip.height);
    }
  } else {
    const half = tip.height / 2;
    const minCenter = VIEW_MARGIN + half;
    const maxCenter = Math.max(minCenter, window.innerHeight - VIEW_MARGIN - half);
    const clamped = Math.min(Math.max(top, minCenter), maxCenter);
    arrowShift = top - clamped;
    top = clamped;

    if (placement === 'left') {
      if (left - tip.width < VIEW_MARGIN) {
        left = VIEW_MARGIN + tip.width;
      }
    } else if (left + tip.width > window.innerWidth - VIEW_MARGIN) {
      left = Math.max(VIEW_MARGIN, window.innerWidth - VIEW_MARGIN - tip.width);
    }
  }

  return { top, left, arrowShift };
}

export function Tooltip({
  content,
  children,
  delayMs = 120,
  placement = 'right',
  disabled = false,
  triggerClassName,
  nowrap = false,
}: TooltipProps) {
  const tipId = useId();
  const triggerRef = useRef<HTMLSpanElement>(null);
  const tipRef = useRef<HTMLSpanElement>(null);
  const showTimer = useRef<ReturnType<typeof setTimeout> | null>(null);
  const [open, setOpen] = useState(false);
  const [coords, setCoords] = useState<Coords | null>(null);

  const clearTimer = () => {
    if (showTimer.current != null) {
      clearTimeout(showTimer.current);
      showTimer.current = null;
    }
  };

  const updatePosition = useCallback(() => {
    const el = triggerRef.current;
    if (!el) return;
    const desired = computePosition(el.getBoundingClientRect(), placement);
    const tipEl = tipRef.current;
    if (tipEl) {
      setCoords(clampToViewport(desired, tipEl.getBoundingClientRect(), placement));
    } else {
      setCoords(desired);
    }
  }, [placement]);

  const show = () => {
    if (disabled || content == null || content === '') return;
    clearTimer();
    showTimer.current = setTimeout(() => {
      updatePosition();
      setOpen(true);
    }, delayMs);
  };

  const hide = () => {
    clearTimer();
    setOpen(false);
  };

  useEffect(() => () => clearTimer(), []);

  useLayoutEffect(() => {
    if (!open) return;
    updatePosition();
  }, [open, updatePosition, content, nowrap]);

  useEffect(() => {
    if (!open) return;
    const onScrollOrResize = () => updatePosition();
    window.addEventListener('scroll', onScrollOrResize, true);
    window.addEventListener('resize', onScrollOrResize);
    return () => {
      window.removeEventListener('scroll', onScrollOrResize, true);
      window.removeEventListener('resize', onScrollOrResize);
    };
  }, [open, updatePosition]);

  const tipStyle: CSSProperties | undefined = coords
    ? {
        top: coords.top,
        left: coords.left,
        ['--arrow-shift' as string]: `${coords.arrowShift}px`,
      }
    : undefined;

  return (
    <>
      <span
        ref={triggerRef}
        className={
          triggerClassName ? `${styles.trigger} ${triggerClassName}` : styles.trigger
        }
        onMouseEnter={show}
        onMouseLeave={hide}
        onFocus={show}
        onBlur={hide}
        aria-describedby={open ? tipId : undefined}
      >
        {children}
      </span>
      {open && coords
        ? createPortal(
            <span
              ref={tipRef}
              id={tipId}
              role="tooltip"
              className={`${styles.tip} ${styles[placement]}${nowrap ? ` ${styles.nowrap}` : ''}`}
              style={tipStyle}
            >
              {content}
            </span>,
            document.body,
          )
        : null}
    </>
  );
}
